import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Professional } from '../models/Professionals';

/**
 * TranslationService - Actualizado para Schema v2.0
 * 
 * Maneja la traducción inteligente de campos multilingües en Professional
 * Nueva estructura: professional.content.{field}.es / professional.content.{field}.en
 */
@Injectable({
    providedIn: 'root'
})
export class TranslationService {

    constructor(private translate: TranslateService) { }

    /**
     * Obtiene el texto traducido de un profesional de forma inteligente
     * 
     * Lógica:
     * 1. Si existe texto en el idioma actual → devolver ese texto
     * 2. Si no existe → intentar el otro idioma (fallback)
     * 3. Si tampoco existe → devolver vacío
     * 
     * @param professional - Objeto del profesional
     * @param field - Campo a traducir ('description' o 'bio')
     * @returns Texto en el idioma actual
     */
    getTranslatedField(professional: Professional, field: 'description' | 'bio'): string {
        const currentLang = this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'es';

        // Acceder a la nueva estructura: professional.content.{field}
        const contentField = professional.content?.[field];

        if (!contentField) return '';

        // Obtener el texto en el idioma actual
        const currentText = contentField[currentLang as 'es' | 'en'];

        // Si existe texto en el idioma actual, devolverlo
        if (currentText && currentText.trim() !== '') {
            return currentText;
        }

        // Fallback: intentar el otro idioma
        const fallbackLang = currentLang === 'es' ? 'en' : 'es';
        const fallbackText = contentField[fallbackLang as 'es' | 'en'];

        if (fallbackText && fallbackText.trim() !== '') {
            return fallbackText;
        }

        // Si no hay nada, devolver vacío
        return '';
    }

    /**
     * Obtiene el idioma original del texto (el que tiene contenido)
     * @param professional - Objeto del profesional
     * @param field - Campo a consultar ('description' o 'bio')
     * @returns Código de idioma ('es' o 'en')
     */
    getOriginalLanguage(professional: Professional, field: 'description' | 'bio'): string {
        const contentField = professional.content?.[field];
        if (!contentField) return 'es';

        // Verificar qué idioma tiene contenido
        if (contentField.es && contentField.es.trim() !== '') {
            return 'es';
        }
        if (contentField.en && contentField.en.trim() !== '') {
            return 'en';
        }

        return 'es'; // Default
    }

    /**
     * Verifica si existe traducción para un campo en un idioma específico
     * @param professional - Objeto del profesional
     * @param field - Campo a verificar ('description' o 'bio')
     * @param lang - Idioma a verificar ('es' o 'en')
     * @returns true si existe traducción
     */
    hasTranslation(professional: Professional, field: 'description' | 'bio', lang: 'es' | 'en'): boolean {
        const text = professional.content?.[field]?.[lang];
        return !!(text && text.trim() !== '');
    }

    /**
     * Obtiene el texto en un idioma específico
     * @param professional - Objeto del profesional
     * @param field - Campo a obtener ('description' o 'bio')
     * @param lang - Idioma específico ('es' o 'en')
     * @returns Texto en el idioma solicitado
     */
    getText(professional: Professional, field: 'description' | 'bio', lang: 'es' | 'en'): string {
        return professional.content?.[field]?.[lang] || '';
    }
}
